import {
  locationsQuery,
  useDeleteLocationMutation,
} from "@common/api/locations"
import { ConfirmDialog } from "@common/components/shared/confirm-dialog"
import { formatCount } from "@common/lib/number"
import { handleError } from "@common/utils"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import useCustomToast from "@/hooks/use-custom-toast"
import { descendantLocations } from "@/routes/_layout/settings/_panel/workspace/facilities/-lib/locations"

export const Route = createFileRoute(
  "/_layout/settings/_panel/workspace/facilities/_list/$facilityId/locations/$locationId/delete",
)({
  component: DeleteLocationDialog,
})

function DeleteLocationDialog(): React.ReactElement | null {
  const { facilityId, locationId } = Route.useParams()
  const { data } = useSuspenseQuery(locationsQuery(facilityId))
  const navigate = useNavigate()
  const showToast = useCustomToast()
  const deleteMutation = useDeleteLocationMutation()
  const location = data.data.find((item) => item.id === locationId)

  if (!location) {
    return null
  }

  const close = () =>
    navigate({
      to: "/settings/workspace/facilities/$facilityId",
      params: { facilityId },
      search: true,
    })

  const confirm = () =>
    deleteMutation.mutate(
      { facility_id: facilityId, location_id: locationId },
      {
        onSuccess: () => {
          showToast("Archived", `${location.name} archived.`, "success")
          close()
        },
        onError: (error) => handleError(error, showToast),
      },
    )

  return (
    <ConfirmDialog
      title={`Archive ${location.name}?`}
      description={deleteDescription(
        descendantLocations(locationId, data.data).length,
      )}
      action="Archive"
      tone="default"
      pendingAction="Archiving…"
      pending={deleteMutation.isPending}
      onConfirm={confirm}
      onCancel={close}
    />
  )
}

function deleteDescription(nested: number): string {
  switch (nested) {
    case 0:
      return "This archives the location. You can restore it from the Archived tab."
    case 1:
      return "This archives the location and its child. Restore them individually, parent first."
    default:
      return `This also archives the ${formatCount(nested)} locations inside it. Restore them individually, parent first.`
  }
}
